// OWNER: LG-ADMIN. Small building blocks shared by the Lead Search tabs (on top of components/ui.tsx and admin/ui.tsx).
import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { ProblemList } from '../admin/ui';
import type { Problem } from '../admin/validate';
import { addChips, removeChip } from './profile';
import type { ProgressBar, Tone } from './logic';
import { int, money, usd } from './logic';
import './leadgen.css';

export function ToneChip(props: { tone: Tone; children: ComponentChildren; title?: string }) {
  return <span class={`chip${props.tone ? ` chip-${props.tone}` : ''}`} title={props.title}>{props.children}</span>;
}

/** Money in a table cell or list: whole cents (4 decimals under a cent) via `money`; the exact amount is the tooltip. */
export function Money(props: { usd: number | null | undefined }) {
  const short = money(props.usd);
  const exact = usd(props.usd);
  return <span class="lg-money" title={exact !== short ? exact : undefined}>{short}</span>;
}

/** An evidence or source link: opens in a new tab, never sends a referrer. Renders "—" when there is no URL. */
export function ExtLink(props: { href: string | null | undefined; children: ComponentChildren; title?: string }) {
  if (!props.href) return <span class="muted">—</span>;
  return <a href={props.href} target="_blank" rel="noopener noreferrer" title={props.title ?? props.href}>{props.children}</a>;
}

/** Plain-sentence blockers or problems, as the admin pages show them. */
export function Blockers(props: { items: string[]; level?: 'error' | 'warn' }) {
  if (!props.items.length) return null;
  const level = props.level ?? 'error';
  return <ProblemList problems={props.items.map((message): Problem => ({ level, where: '', message }))} />;
}

/** A 0..1 bar with optional marks (e.g. the 0.85 and 0.95 precision bars). */
export function Meter(props: { value: number | null; marks?: number[]; tone?: 'over' | 'warn' | ''; label?: string }) {
  const v = props.value === null ? 0 : Math.max(0, Math.min(1, props.value));
  return (
    <div class="lg-meter" role="meter" aria-valuemin={0} aria-valuemax={1} aria-valuenow={props.value ?? undefined} aria-label={props.label}>
      <i class={props.tone || ''} style={`width:${(v * 100).toFixed(1)}%`} />
      {(props.marks ?? []).map((m) => <b style={`left:calc(${(m * 100).toFixed(1)}% - 1px)`} title={String(m)} />)}
    </div>
  );
}

export function StageBars(props: { bars: ProgressBar[] }) {
  return (
    <div class="lg-bars">
      {props.bars.map((b) => {
        const pct = b.total > 0 ? Math.round((b.finished / b.total) * 100) : b.state === 'done' ? 100 : 0;
        return (
          <div class={`lg-bar ${b.state}`} title={`${b.label}: ${b.finished} of ${b.total} tasks finished${b.failed ? `, ${b.failed} failed` : ''}`}>
            <span>{b.label}</span>
            <div><i style={`width:${pct}%`} /></div>
            <em class={b.failed ? 'bad' : ''}>{b.total ? `${int(b.finished)}/${int(b.total)}` : b.state === 'done' ? 'none' : '—'}{b.failed ? ` · ${b.failed}✗` : ''}</em>
          </div>
        );
      })}
    </div>
  );
}

/** Editable chips: type and press Enter (or paste a comma list); × removes. `mark` flags items (e.g. unverified). */
export function ChipEditor(props: {
  values: string[]; onChange: (v: string[]) => void; placeholder?: string; disabled?: boolean; label: string;
}) {
  const [draft, setDraft] = useState('');
  // read the input's live value, not `draft`: a paste followed at once by Enter can arrive before the re-render
  const commit = (el: HTMLInputElement) => {
    const raw = el.value;
    if (raw.trim()) { props.onChange(addChips(props.values, raw)); }
    el.value = '';
    setDraft('');
  };
  return (
    <div class="lg-chips" role="group" aria-label={props.label}>
      {props.values.map((v, i) => (
        <span class="chip">
          {v}
          {props.disabled ? null : <button type="button" class="lg-chip-x" aria-label={`Remove ${v}`} onClick={() => props.onChange(removeChip(props.values, i))}>×</button>}
        </span>
      ))}
      {props.disabled ? null : (
        <input class="lg-chip-input" value={draft} placeholder={props.placeholder ?? 'Type and press Enter'} aria-label={`Add to ${props.label}`}
          onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => {
            const el = e.currentTarget as HTMLInputElement;
            if (e.key === 'Enter') { e.preventDefault(); commit(el); }
            else if (e.key === 'Backspace' && !el.value && props.values.length) props.onChange(props.values.slice(0, -1));
          }}
          onBlur={(e) => { if ((e.currentTarget as HTMLInputElement).value.trim()) commit(e.currentTarget as HTMLInputElement); }} />
      )}
    </div>
  );
}

/** Run `fn` every `ms` while `enabled` and the tab is visible. */
export function usePoll(fn: () => void, ms: number, enabled: boolean): void {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => { if (!document.hidden) ref.current(); }, ms);
    return () => clearInterval(id);
  }, [enabled, ms]);
}

/** Seconds since `since` (for the ~10 s Opus proposal wait). */
export function useElapsed(since: number | null): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (since === null) return;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [since]);
  return since === null ? 0 : Math.max(0, Math.floor((now - since) / 1000));
}
